import { useCallback, useEffect, useState, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { saveReturnTo } from '@/utils/returnTo';
import { purgeAllDrafts } from '@/hooks/useDraft';

/**
 * HIPAA-Compliant Session Timeout Hook
 *
 * Per HIPAA Security Rule CFR § 164.312(a)(2)(iii):
 * "Implement electronic procedures that terminate an electronic session
 * after a predetermined time of inactivity."
 *
 * Best practices recommend 10-15 minutes for systems accessing ePHI;
 * this deployment uses a 30-minute idle timeout (product decision) with a 2-minute warning.
 * This is an ACCEPTED RISK, reconfirmed by the project owner 2026-08-03 — not drift.
 * Do not lower it to 15 minutes on the assumption it is a mistake. See docs/SECURITY.md sections 6 and 10.
 *
 * IMPORTANT: This is a custom implementation that does NOT rely on react-idle-timer
 * which had issues with disabled state changes. This uses a simple, reliable
 * timestamp-based approach.
 */

// Configuration constants
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes idle timeout
const WARNING_BEFORE_LOGOUT_MS = 2 * 60 * 1000; // Show warning 2 minutes before logout (relative → warns at 28 min)
const CHECK_INTERVAL_MS = 1000; // Check every second

// Storage key for cross-tab sync
const LAST_ACTIVITY_KEY = 'hipaa_last_activity';

interface UseSessionTimeoutOptions {
  onIdle?: () => void;
  onActive?: () => void;
  onPrompt?: () => void;
}

interface UseSessionTimeoutReturn {
  isIdle: boolean;
  isPrompted: boolean;
  remainingTime: number;
  handleStayLoggedIn: () => void;
  handleLogoutNow: () => void;
}

// Get last activity timestamp (cross-tab via sessionStorage)
const getLastActivity = (): number => {
  const stored = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  return stored ? parseInt(stored, 10) : Date.now();
};

// Set last activity timestamp
const setLastActivity = (time: number = Date.now()) => {
  sessionStorage.setItem(LAST_ACTIVITY_KEY, time.toString());
};

export const useSessionTimeout = (
  options: UseSessionTimeoutOptions = {}
): UseSessionTimeoutReturn => {
  const { onIdle, onActive, onPrompt } = options;
  const logout = useAuthStore((state) => state.logout);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const [remainingTime, setRemainingTime] = useState(IDLE_TIMEOUT_MS);
  const [isPrompted, setIsPrompted] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const wasPromptedRef = useRef(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Force logout handler
  const forceLogout = useCallback(async () => {
    console.log('[HIPAA Session] Session expired due to inactivity - FORCING LOGOUT');
    setIsPrompted(false);
    setIsIdle(true);
    onIdle?.();

    // Clear activity tracking
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);

    // CRITICAL: Set a flag to prevent auto-login on page reload
    // This flag is checked in authStore.initialize() to skip session recovery
    sessionStorage.setItem('hipaa_session_expired', 'true');

    // Beacon fallback (fires even if the page is torn down before the awaited POST
    // completes): records session.logout.idle. Sent urlencoded so the cross-origin
    // beacon is a CORS-simple request (no preflight). Read the token BEFORE logout()
    // clears it. Backend dedupes this against the primary POST below (30s/user).
    try {
      const token = sessionStorage.getItem('authToken');
      if (token && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.proviarcm.com/api';
        const params = new URLSearchParams();
        params.set('token', token);
        params.set('reason', 'idle');
        navigator.sendBeacon(`${apiBase}/auth/logout-event`, params);
      }
    } catch (beaconErr) {
      console.warn('[HIPAA Session] logout beacon failed (continuing):', beaconErr);
    }

    // Perform logout and WAIT for it to complete
    // This ensures the backend session cookie is invalidated before redirect.
    // Reason 'idle': the session dies and the stored Google tokens are cleared, but
    // the Google grant is left alone — walking away from a desk should not cost the
    // person a consent screen when they come back. Deliberate sign-out is what
    // revokes; see backend googleTokenRevocationService.shouldRevokeGrant.
    try {
      await logout('idle');
    } catch (error) {
      console.error('[HIPAA Session] Logout error (continuing anyway):', error);
    }

    // Preserve the current deposit search/filters/page so re-login restores it
    // (involuntary timeout — Ashley never chose to leave this view).
    saveReturnTo();

    // Redirect to login with session expired message
    window.location.href = '/login?session=expired';
  }, [logout, onIdle]);

  // Reset activity on user interaction
  const resetActivity = useCallback(() => {
    if (!isAuthenticated) return;

    // Reset timer and dismiss warning on ANY user activity
    // UI says "Move mouse or press any key to continue" - honor that promise
    if (wasPromptedRef.current) {
      console.log('[HIPAA Session] User activity detected during warning - resetting timer');
      setIsPrompted(false);
      wasPromptedRef.current = false;
    }

    setLastActivity();
    setRemainingTime(IDLE_TIMEOUT_MS);
    onActive?.();
  }, [isAuthenticated, onActive]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up when not authenticated
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Initialize last activity
    setLastActivity();
    console.log('[HIPAA Session] Session timeout monitoring STARTED - 30 minute timeout');

    // CRITICAL: Check timeout immediately when tab becomes visible
    // This runs BEFORE activity events (click, mousedown) can reset the timer
    // Browsers throttle setInterval in background tabs, so we must check on visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const lastActivity = getLastActivity();
        const elapsed = Date.now() - lastActivity;
        const remaining = Math.max(0, IDLE_TIMEOUT_MS - elapsed);

        console.log('[HIPAA Session] Tab became visible. Elapsed:', Math.floor(elapsed / 1000), 'seconds');

        // Update UI immediately (interval was throttled while backgrounded)
        setRemainingTime(remaining);

        // Check if we should show warning now
        if (remaining <= WARNING_BEFORE_LOGOUT_MS && remaining > 0 && !wasPromptedRef.current) {
          console.log('[HIPAA Session] WARNING on tab focus: Session will expire in', Math.ceil(remaining / 1000), 'seconds');
          setIsPrompted(true);
          wasPromptedRef.current = true;
          onPrompt?.();
        }

        // Force logout if expired
        if (remaining === 0) {
          console.log('[HIPAA Session] TIMEOUT DETECTED on tab focus - forcing logout');
          forceLogout();
          return;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel',
    ];

    // Throttled activity handler (max once per second)
    let lastReset = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset > 1000) {
        lastReset = now;
        resetActivity();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Check timer periodically
    checkIntervalRef.current = setInterval(() => {
      const lastActivity = getLastActivity();
      const elapsed = Date.now() - lastActivity;
      const remaining = Math.max(0, IDLE_TIMEOUT_MS - elapsed);

      setRemainingTime(remaining);

      // Check if we should show warning (2 minutes before logout)
      if (remaining <= WARNING_BEFORE_LOGOUT_MS && remaining > 0 && !wasPromptedRef.current) {
        console.log('[HIPAA Session] WARNING: Session will expire in', Math.ceil(remaining / 1000), 'seconds');
        setIsPrompted(true);
        wasPromptedRef.current = true;
        onPrompt?.();
      }

      // Check if we should force logout
      if (remaining === 0) {
        forceLogout();
      }
    }, CHECK_INTERVAL_MS);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, resetActivity, forceLogout, onPrompt]);

  // Handle user clicking "Stay Logged In"
  const handleStayLoggedIn = useCallback(() => {
    console.log('[HIPAA Session] User requested to stay logged in');
    setLastActivity();
    setIsPrompted(false);
    wasPromptedRef.current = false;
    setRemainingTime(IDLE_TIMEOUT_MS);
  }, []);

  // Handle user clicking "Log Out Now"
  const handleLogoutNow = useCallback(() => {
    console.log('[HIPAA Session] User requested immediate logout');
    setIsPrompted(false);
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    // VOLUNTARY logout: purge all unsaved drafts (the user chose to leave).
    // NOTE: forceLogout() (idle timeout) deliberately does NOT purge them — that
    // is the whole session-restore feature.
    purgeAllDrafts();
    // 'user', not 'idle': the timer may have prompted, but a person clicked the
    // button. This is the deliberate sign-out that retires the Google grant.
    logout('user');
    window.location.href = '/login?logout=manual';
  }, [logout]);

  return {
    isIdle,
    isPrompted,
    remainingTime,
    handleStayLoggedIn,
    handleLogoutNow,
  };
};

export default useSessionTimeout;

// Export constants for use in UI
export const SESSION_TIMEOUT_MINUTES = IDLE_TIMEOUT_MS / 60000;
export const WARNING_MINUTES = WARNING_BEFORE_LOGOUT_MS / 60000;
